// src/utils.js
import React from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { parseISO, format } from "date-fns";

/**
 * Generates a consistent Medusa-inspired color based on the input string.
 * @param {string} nom - The input string to generate color from.
 * @returns {string} - A hex color code.
 */
export const getMedusaInspiredColor = (nom) => {
  if (!nom || typeof nom !== "string" || nom.trim() === "") {
    return "#CCCCCC"; // Default color instead of "NA"
  }
  const medusaColors = [
    // Pastels & light accents
    "#66C0B8", "#94D3CD", "#C9E6CA", "#CEE6B3", "#D1ECE9",
    "#B3DDB5", "#BCC8CE",
  
    // Bleus
    "#2F4E8B", // bleu marine (principal)
    "#1C2E5A", // bleu marine foncé (fonds)
    "#5DAFE5", "#3FA7D6", "#95DBFA", "#A2D3FA", "#BCDFFB",
  
    // Violets
    "#6C3FB7", "#8A2BE2", "#AFB6E0", "#D1C4E9", "#D6A4DE",
  
    // Jaunes & orangés
    "#FFD494", "#FFE695", "#FFECB4", "#FFF7AD", "#FFD166",
    "#F9B24E", "#F4D06F",
  
    // Corail / rouge doux
    "#E85A71",
  
    // Darker spectrum
    // Deep Blues & Cyans
    "#0A1F2A", "#102A44", "#13475F", "#1A5468",
  
    // Dark Purples & Magentas
    "#2C1947", "#3B2A5C", "#4A376F", "#5C3A6E",
  
    // Burnt Oranges & Corals
    "#7A2E25", "#8E3B46", "#9C3D1F",
  
    // Deep Yellows & Golds
    "#6B4F1D", "#80661E", "#8C6F22",
  
    // Accents (almost black)
    "#0F0D0E", "#1B1A1C",
  
    // **New additions** (dark, saturated)
    // Extra Bleu Marine
    "#001F3F", "#0B3D91",
    // Extra Oranges brûlés
    "#CC5500", "#FF4500",
    // Extra Verts foncés
    "#004225", "#006400",
    // Extra Teal foncé
    "#025959"
  ];
  

  const hash = [...nom].reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const index = hash % medusaColors.length;
  return medusaColors[index];
};

/**
 * Extracts initials from a given name.
 * @param {string} nom - The full name.
 * @returns {string} - Initials (max 2 characters).
 */
export const getInitials = (nom) => {
  if (!nom || typeof nom !== "string" || nom.trim() === "") {
    return "NA";
  }
  const nameParts = nom.split(" ");
  const initials = nameParts
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
  return initials.slice(0, 2);
};

/**
 * Masks sensitive data.
 * @param {string} data - The data to be masked.
 * @returns {string} - Masked data.
 */
export const maskData = (data) => "********";

/**
 * Formats a date string to "MMMM yyyy" format.
 * @param {string} dateString - The ISO date string.
 * @returns {string} - Formatted date.
 */
export const formatDate = (dateString) => {
  try {
    return format(parseISO(dateString), "MMMM yyyy");
  } catch (error) {
    return "";
  }
};

/**
 * Normalizes experiences data.
 * @param {Array} experiences - Array of experience objects.
 * @returns {Array} - Normalized experiences.
 */
export const normalizeExperiences = (experiences) => {
  return experiences.map((experience) => {
    if (experience.startDate) {
      const company = experience.company || "Unknown Company";
      const formattedStartDate = experience.startDate ? formatDate(experience.startDate) : "";
      const formattedEndDate = experience.endDate ? formatDate(experience.endDate) : "";
      return {
        title: experience.title,
        company: company,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        duration: experience.duration,
        logoUrl: experience.logoUrl,
      };
    } else if (experience.duration) {
      return {
        title: experience.title,
        company: experience.company,
        startDate: "",
        endDate: "",
        duration: experience.duration,
        logoUrl: experience.logoUrl,
      };
    } else {
      return {
        title: experience.title,
        company: experience.company,
        startDate: "",
        endDate: "",
        duration: "",
        logoUrl: experience.logoUrl,
      };
    }
  });
};

/**
 * Sorts experiences by most recent.
 * @param {Array} experiences - Array of normalized experience objects.
 * @returns {Array} - Sorted experiences.
 */
export const sortExperiences = (experiences) => {
  return experiences.sort((a, b) => {
    const dateA = new Date(a.endDate === "Present" ? new Date() : a.endDate);
    const dateB = new Date(b.endDate === "Present" ? new Date() : b.endDate);
    return dateB - dateA;
  });
};

/**
 * Helper function to get color based on role.
 * @param {string} role - The role of the user.
 * @returns {string} - The color associated with the role.
 */
export const getColorForRole = (role) => {
  switch (role) {
    case "ROLE_SUPER_ADMIN":
      return "#36bcff";
    case "ROLE_ADMIN":
      return "#94cfec";
    case "ROLE_COLLABORATOR":
      return "#9ebac8";
    default:
      return "#bcbcbc";
  }
};

/**
 * Helper function to get color based on subscription.
 * @param {boolean} isPremium - The subscription status of the user.
 * @returns {string} - The color associated with the subscription status.
 */
export const getColorForSubscription = (isPremium) => {
  return isPremium ? "#ffd700" : "#b0c4de"; // Gold for Premium, LightSteelBlue for Standard
};

/**
 * Helper function to transform role string into a more readable label.
 * @param {string} role - The role of the user.
 * @returns {string} - The readable label for the role.
 */
export const getLabelForRole = (role) => {
  switch (role) {
    case "ROLE_SUPER_ADMIN":
      return "Super Administrateur";
    case "ROLE_ADMIN":
      return "Administrateur";
    case "ROLE_COLLABORATOR":
      return "Collaborateur";
    default:
      return "Freelance";
  }
};

/**
 * Helper function to get color based on status.
 * @param {string} status - The status of the user.
 * @returns {string} - The color associated with the status.
 */
export const getColorForStatus = (status) => {
    switch (status) {
        case "retenue":
            return "#2F4E8B"; // Green
        case "en attente":
            return "#ffad5b"; // Yellow
        case "refusée":
            return "#ea4646"; // Red
        default:
            return "#9e9e9e"; // Grey for unknown statuses
    }
};

// ====================================================
// Route Protection Utilities: Authentication Helpers
// ====================================================

/**
 * Custom hook to check if a user is authenticated by verifying if a token exists in local storage
 * and if the current user's status is 'in use'.
 * @returns {boolean} - True if the user is authenticated and their status is 'in use', false otherwise.
 */
export const useIsAuthenticated = () => {
  const token = localStorage.getItem("token");
  const { currentUser } = useSelector((state) => state.users);
  if (!token || !currentUser) return false;

  return currentUser.status === "in use";
};

/**
 * Higher-order component to protect routes that require authentication.
 * If the user is not authenticated, they will be redirected to the unauthorized page ("/interdit").
 * If the user's status is 'creation' or 'chgPwd', they will be redirected to the 2FA page ("/2fa").
 *
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - The component(s) to render if authenticated.
 * @returns {React.ReactNode} - Rendered children or a redirect.
 */
export const ProtectedRoute = ({ children, requiredStatus }) => {
  const token = localStorage.getItem("token");
  const { currentUser } = useSelector((state) => state.users);

  if (!token || !currentUser) {
    return <Navigate to="/interdit" replace />;
  }

  if (currentUser.status === "creation" || currentUser.status === "chgPwd") {
    return <Navigate to="/2fa" replace />;
  }

  return children;
};
